import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Table, Alert, Spinner, InputGroup, Row, Col, Badge } from 'react-bootstrap';
import Swal from 'sweetalert2';
import moment from 'moment';
import { getUltimasCalibraciones } from '../../../api/common';
import ModalCorregirEquipos from './ModalCorregirEquipos';
import FiltroFechaExcel from './FiltroFechaExcel';

const RESUMEN_VACIO = { total: 0, vencidos: 0, proximos: 0, vigentes: 0, enProceso: 0, sinCalibrar: 0 };

const ESTADOS_VENCIMIENTO = [
    { value: 'vencido', label: 'Vencido', variant: 'danger' },
    { value: 'proximo', label: 'Próximo', variant: 'warning' },
    { value: 'vigente', label: 'Vigente', variant: 'success' },
    { value: 'en-proceso', label: 'En proceso', variant: 'info' },
    { value: 'sin-calibrar', label: 'Sin calibrar', variant: 'secondary' },
];

const BadgeVencimiento = ({ vencimiento }) => {
    const cfg = ESTADOS_VENCIMIENTO.find(e => e.value === vencimiento);
    if (!cfg) return null;
    return (
        <Badge bg={cfg.variant} text={cfg.variant === 'warning' ? 'dark' : undefined}>
            {cfg.label}
        </Badge>
    );
};

/**
 * EquipoUltimasCal — Tab "Últimas Cal." de AgregarEquiposOt.
 * Muestra los equipos activos del cliente con su última calibración registrada,
 * semáforo de vencimiento y filtros combinables (Item, Sede, Estado, texto y
 * fecha estilo Excel multi-selección año→mes).
 */
const EquipoUltimasCal = ({
    clienteId,
    equiposYaAgregados = [],
    onAgregar,
    onResolverDependientes,
    refreshTrigger = 0,
    listaSedes = [],
    onCorregir,
    initialData = null,
    onDataLoaded = null,
    onResumenChange = null,
}) => {
    const [equipos, setEquipos] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filtroItem, setFiltroItem] = useState('');
    const [filtroSede, setFiltroSede] = useState('');
    const [filtroEstado, setFiltroEstado] = useState([]);
    const [filtroFechas, setFiltroFechas] = useState([]);
    const [sortDir, setSortDir] = useState('desc');

    const [selected, setSelected] = useState([]);
    const [resumen, setResumen] = useState(RESUMEN_VACIO);

    const [showModalCorregir, setShowModalCorregir] = useState(false);
    const [equiposProblematicos, setEquiposProblematicos] = useState([]);
    const [equiposCorregidosCache, setEquiposCorregidosCache] = useState(new Map());

    const limpiarFiltros = () => {
        setFiltroItem('');
        setFiltroSede('');
        setFiltroEstado([]);
        setFiltroFechas([]);
        setSearchTerm('');
    };

    // Carga inicial: usar caché del padre si aplica; si no, fetch.
    // Patrón isMounted para descartar respuestas obsoletas al cambiar clienteId
    // o al desmontar (el wrapper request() no soporta AbortController todavía).
    useEffect(() => {
        if (!clienteId) {
            setEquipos([]);
            setFiltered([]);
            setSelected([]);
            setResumen(RESUMEN_VACIO);
            limpiarFiltros();
            return;
        }

        if (initialData && initialData.clienteId === clienteId && initialData.data) {
            setEquipos(initialData.data);
            setFiltered(initialData.data);
            setResumen(initialData.resumen || RESUMEN_VACIO);
            setSelected([]);
            return;
        }

        let isMounted = true;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const response = await getUltimasCalibraciones({ clienteId });
                if (!isMounted) return;
                const data = response?.data?.data || [];
                const resumenData = response?.data?.resumen || RESUMEN_VACIO;
                setEquipos(data);
                setFiltered(data);
                setResumen(resumenData);
                setSelected([]);
                onDataLoaded?.(data, resumenData);
            } catch (err) {
                if (!isMounted) return;
                setError('Error al cargar las últimas calibraciones: ' + (err.response?.data?.message || err.message));
            } finally {
                if (isMounted) setLoading(false);
            }
        })();

        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clienteId]);

    // Refresh: recargar cuando refreshTrigger cambia
    useEffect(() => {
        if (refreshTrigger > 0 && clienteId) {
            let isMounted = true;
            setLoading(true);
            setError(null);
            (async () => {
                try {
                    const response = await getUltimasCalibraciones({ clienteId });
                    if (!isMounted) return;
                    const data = response?.data?.data || [];
                    const resumenData = response?.data?.resumen || RESUMEN_VACIO;
                    setEquipos(data);
                    setFiltered(data);
                    setResumen(resumenData);
                    setSelected([]);
                    onDataLoaded?.(data, resumenData);
                } catch (err) {
                    if (!isMounted) return;
                    setError('Error al recargar: ' + (err.response?.data?.message || err.message));
                } finally {
                    if (isMounted) setLoading(false);
                }
            })();
            return () => { isMounted = false; };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger]);

    // Debounce del buscador (300ms) — el <input> sigue controlado por searchTerm
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Propagar resumen al padre (para el badge del tab)
    useEffect(() => {
        onResumenChange?.(resumen);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumen]);

    // Derivadas para filtros
    const listaItems = useMemo(() => {
        const unicos = new Map();
        equipos.forEach(fila => {
            const it = fila.equipo?.item;
            if (it && it._id) unicos.set(it._id, it.nombre);
        });
        return Array.from(unicos.entries())
            .map(([id, nombre]) => ({ id, nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [equipos]);

    const sedesEnEquipos = useMemo(() => {
        const unicas = new Map();
        equipos.forEach(fila => {
            const sede = fila.equipo?.sedeClient;
            if (sede && sede._id) unicas.set(sede._id, sede.nameSede);
        });
        return Array.from(unicas.entries())
            .map(([id, nombre]) => ({ id, nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [equipos]);

    const fechasParaFiltro = useMemo(() => {
        return equipos
            .map(fila => fila.ultimaCalibracion?.ultimaFecha)
            .filter(Boolean);
    }, [equipos]);

    // Filtrado + ordenamiento (AND lógico entre filtros)
    useEffect(() => {
        let result = [...equipos];

        if (filtroItem) {
            result = result.filter(f => f.equipo?.item?._id === filtroItem);
        }
        if (filtroSede) {
            result = result.filter(f => f.equipo?.sedeClient?._id === filtroSede);
        }
        if (filtroEstado.length > 0) {
            result = result.filter(f => filtroEstado.includes(f.vencimiento));
        }
        if (filtroFechas.length > 0) {
            result = result.filter(f => {
                if (!f.ultimaCalibracion?.ultimaFecha) return false;
                const m = moment(f.ultimaCalibracion.ultimaFecha);
                return filtroFechas.some(sel => sel.anio === m.year() && sel.meses.includes(m.month()));
            });
        }
        if (debouncedSearchTerm.trim() !== '') {
            const term = debouncedSearchTerm.toLowerCase();
            result = result.filter(f => {
                const eq = f.equipo || {};
                const cons = f.ultimaCalibracion?.consecutivo || '';
                return (
                    (eq.serie || '').toLowerCase().includes(term) ||
                    (eq.marca || '').toLowerCase().includes(term) ||
                    (eq.modelo || '').toLowerCase().includes(term) ||
                    (eq.item?.nombre || '').toLowerCase().includes(term) ||
                    cons.toLowerCase().includes(term)
                );
            });
        }

        // Partición explícita: los "sin calibrar" SIEMPRE al final del listado.
        const conFecha = result.filter(f => f.ultimaCalibracion?.ultimaFecha);
        const sinFecha = result.filter(f => !f.ultimaCalibracion?.ultimaFecha);

        conFecha.sort((a, b) => {
            const diff = moment(a.ultimaCalibracion.ultimaFecha).valueOf() -
                         moment(b.ultimaCalibracion.ultimaFecha).valueOf();
            if (diff !== 0) return sortDir === 'asc' ? diff : -diff;
            return (a.equipo?.item?.nombre || '').localeCompare(b.equipo?.item?.nombre || '');
        });
        sinFecha.sort((a, b) =>
            (a.equipo?.item?.nombre || '').localeCompare(b.equipo?.item?.nombre || '')
        );

        setFiltered([...conFecha, ...sinFecha]);
    }, [equipos, filtroItem, filtroSede, filtroEstado, filtroFechas, debouncedSearchTerm, sortDir]);

    // Selección
    const isAlreadyAdded = (equipoId) => equiposYaAgregados.some(e => e._id === equipoId);
    const isSelected = (equipoId) => selected.some(e => e._id === equipoId);

    const handleToggle = (equipo) => {
        setSelected(prev => {
            const exists = prev.find(e => e._id === equipo._id);
            if (exists) return prev.filter(e => e._id !== equipo._id);
            return [...prev, equipo];
        });
    };

    const handleToggleAll = (checked) => {
        const seleccionablesFiltro = filtered
            .filter(f => !isAlreadyAdded(f.equipo._id))
            .map(f => f.equipo);
        if (checked) {
            const fueraDelFiltro = selected.filter(
                eq => !filtered.some(f => f.equipo._id === eq._id)
            );
            setSelected([...fueraDelFiltro, ...seleccionablesFiltro]);
        } else {
            setSelected(prev => prev.filter(
                eq => !filtered.some(f => f.equipo._id === eq._id)
            ));
        }
    };

    const equiposSeleccionablesFiltro = filtered.filter(f => !isAlreadyAdded(f.equipo._id));
    const allSelected = equiposSeleccionablesFiltro.length > 0 &&
        equiposSeleccionablesFiltro.every(f => isSelected(f.equipo._id));
    const seleccionadosEnFiltro = filtered.filter(f => isSelected(f.equipo._id)).length;

    const hayFiltrosActivos =
        Boolean(filtroItem) || Boolean(filtroSede) ||
        filtroEstado.length > 0 || filtroFechas.length > 0 || Boolean(searchTerm);

    const detectarEquiposSinSede = (equiposARevisar) =>
        equiposARevisar.filter(eq => !eq.sedeClient || !eq.sedeClient._id || !eq.sedeClient.nameSede);

    const handleAgregar = async () => {
        if (selected.length === 0) return;
        const yaAgregadosIds = equiposYaAgregados.map(e => e._id);
        const equiposNuevos = selected.filter(e => !yaAgregadosIds.includes(e._id));

        if (equiposNuevos.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Equipos ya agregados',
                text: 'Todos los equipos seleccionados ya están en la lista'
            });
            return;
        }
        if (equiposNuevos.length < selected.length) {
            const duplicados = selected.length - equiposNuevos.length;
            Swal.fire({
                icon: 'info',
                title: 'Algunos equipos ya agregados',
                text: `${equiposNuevos.length} equipos agregados. ${duplicados} ya estaban en la lista.`
            });
        }

        // Resolución de dependientes ANTES de detectarEquiposSinSede
        // (spec items-adicionales-equipo-dependiente §Requirement inclusión OT).
        let equiposParaSede = equiposNuevos;
        if (typeof onResolverDependientes === 'function') {
            const idsUnion = [...yaAgregadosIds, ...equiposNuevos.map(e => e._id)];
            const resolverRes = await onResolverDependientes(idsUnion);
            if (resolverRes?.cancelado) return; // preserva selección
            if (resolverRes?.incluirTodos && Array.isArray(resolverRes.extra) && resolverRes.extra.length > 0) {
                const yaEstoyIds = new Set([...yaAgregadosIds, ...equiposNuevos.map(e => e._id)].map(String));
                for (const ex of resolverRes.extra) {
                    if (!yaEstoyIds.has(String(ex._id))) {
                        equiposParaSede = [...equiposParaSede, ex];
                    }
                }
            }
        }

        const problematicos = detectarEquiposSinSede(equiposParaSede);
        if (problematicos.length > 0) {
            setEquiposProblematicos(problematicos);
            setShowModalCorregir(true);
            return;
        }

        onAgregar(equiposParaSede);
        setSelected([]);
    };

    const handleModalClose = () => {
        setShowModalCorregir(false);
        const yaAgregadosIds = equiposYaAgregados.map(e => e._id);
        const equiposParaAgregar = selected.map(equipo => {
            const correccion = equiposCorregidosCache.get(equipo._id);
            return correccion || equipo;
        });
        const equiposNuevos = equiposParaAgregar.filter(e => !yaAgregadosIds.includes(e._id));
        if (equiposNuevos.length > 0) onAgregar(equiposNuevos);
        setSelected([]);
        setEquiposCorregidosCache(new Map());
    };

    const handleModalCancelar = () => {
        setShowModalCorregir(false);
        setSelected([]);
        setEquiposCorregidosCache(new Map());
    };

    return (
        <div>
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {!clienteId ? (
                <Alert variant="warning">
                    Debe seleccionar un cliente para ver sus últimas calibraciones
                </Alert>
            ) : (
                <>
                    {!loading && equipos.length > 0 && (
                        <div className="mb-3 d-flex gap-2 flex-wrap">
                            <Badge bg="danger">{resumen.vencidos} vencidos</Badge>
                            <Badge bg="warning" text="dark">{resumen.proximos} próximos</Badge>
                            <Badge bg="success">{resumen.vigentes} vigentes</Badge>
                            <Badge bg="info">{resumen.enProceso || 0} en proceso</Badge>
                            <Badge bg="secondary">{resumen.sinCalibrar} sin calibrar</Badge>
                        </div>
                    )}

                    <Row className="mb-3 g-2">
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Item</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={filtroItem}
                                    onChange={e => setFiltroItem(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="">Todos</option>
                                    {listaItems.map(i => (
                                        <option key={i.id} value={i.id}>{i.nombre}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Sede</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={filtroSede}
                                    onChange={e => setFiltroSede(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="">Todas</option>
                                    {sedesEnEquipos.map(s => (
                                        <option key={s.id} value={s.id}>{s.nombre}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small mb-1">Estado de vencimiento</Form.Label>
                            <div className="d-flex flex-wrap gap-1">
                                {ESTADOS_VENCIMIENTO.map(({ value: v, label, variant }) => (
                                    <Form.Check
                                        key={v}
                                        type="checkbox"
                                        id={`estado-${v}`}
                                        className="me-1"
                                        label={<Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>{label}</Badge>}
                                        checked={filtroEstado.includes(v)}
                                        onChange={() => setFiltroEstado(prev =>
                                            prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]
                                        )}
                                        disabled={loading}
                                    />
                                ))}
                            </div>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                            <FiltroFechaExcel
                                fechas={fechasParaFiltro}
                                value={filtroFechas}
                                onChange={setFiltroFechas}
                                disabled={loading}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Buscar</Form.Label>
                                <InputGroup size="sm">
                                    <InputGroup.Text>🔍</InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Serie, marca, consecutivo..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        disabled={loading}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={1} className="d-flex align-items-end">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={limpiarFiltros}
                                disabled={loading || !hayFiltrosActivos}
                                title="Limpiar filtros"
                            >
                                <i className="bi bi-x-circle"></i>
                            </Button>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <p className="mt-2">Cargando últimas calibraciones...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <Alert variant="info">
                            {equipos.length === 0
                                ? 'Este cliente no tiene equipos registrados'
                                : 'No se encontraron equipos con los criterios de filtrado'}
                        </Alert>
                    ) : (
                        <>
                            <div className="mb-2 d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    {selected.length} seleccionado(s) en total | {seleccionadosEnFiltro} de {filtered.length} en filtro actual
                                </small>
                                {hayFiltrosActivos && (
                                    <small className="text-primary">
                                        <i className="bi bi-funnel-fill"></i> Filtros activos
                                    </small>
                                )}
                            </div>

                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <Table striped bordered hover size="sm">
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                        <tr>
                                            <th>#</th>
                                            <th style={{ width: '50px', textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    onChange={e => handleToggleAll(e.target.checked)}
                                                    disabled={equiposSeleccionablesFiltro.length === 0}
                                                    style={{ transform: 'scale(1.2)' }}
                                                />
                                            </th>
                                            <th>Item</th>
                                            <th>Marca</th>
                                            <th>Modelo</th>
                                            <th>Serie</th>
                                            <th>Sede</th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                                                title="Ordenar por fecha"
                                            >
                                                Última Fecha {sortDir === 'asc' ? '▲' : '▼'}
                                            </th>
                                            <th>Días</th>
                                            <th>Estado</th>
                                            <th>Consecutivo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((fila, index) => {
                                            const eq = fila.equipo;
                                            const yaAgregado = isAlreadyAdded(eq._id);
                                            const claseFila =
                                                fila.vencimiento === 'vencido' ? 'table-danger' :
                                                fila.vencimiento === 'proximo' ? 'table-warning' :
                                                fila.vencimiento === 'en-proceso' ? 'table-info' :
                                                fila.vencimiento === 'sin-calibrar' ? 'text-muted' : '';
                                            return (
                                                <tr
                                                    key={eq._id}
                                                    onClick={() => !yaAgregado && handleToggle(eq)}
                                                    className={`${claseFila} ${isSelected(eq._id) ? 'table-active' : ''}`.trim()}
                                                    style={{
                                                        cursor: yaAgregado ? 'not-allowed' : 'pointer',
                                                        opacity: yaAgregado ? 0.5 : 1,
                                                    }}
                                                >
                                                    <td>{index + 1}</td>
                                                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected(eq._id)}
                                                            onChange={() => !yaAgregado && handleToggle(eq)}
                                                            disabled={yaAgregado}
                                                            style={{ transform: 'scale(1.2)' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        {eq.item?.nombre || 'N/A'}
                                                        {yaAgregado && <span className="badge bg-secondary ms-2">Ya agregado</span>}
                                                    </td>
                                                    <td>{eq.marca || 'N/A'}</td>
                                                    <td>{eq.modelo || 'N/A'}</td>
                                                    <td>{eq.serie}</td>
                                                    <td>{eq.sedeClient?.nameSede || 'N/A'}</td>
                                                    <td>
                                                        {fila.ultimaCalibracion?.ultimaFecha
                                                            ? moment(fila.ultimaCalibracion.ultimaFecha).format('DD/MM/YYYY')
                                                            : '—'}
                                                    </td>
                                                    <td>{fila.diasDesdeUltimaCal ?? '—'}</td>
                                                    <td><BadgeVencimiento vencimiento={fila.vencimiento} /></td>
                                                    <td>{fila.ultimaCalibracion?.consecutivo || '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-grid mt-3">
                                <Button
                                    variant="primary"
                                    onClick={handleAgregar}
                                    disabled={selected.length === 0}
                                >
                                    Agregar Seleccionados ({selected.length})
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}

            <ModalCorregirEquipos
                show={showModalCorregir}
                equiposProblematicos={equiposProblematicos}
                listaSedes={listaSedes}
                onCorregir={(equipoId, datosActualizados) => {
                    setEquiposCorregidosCache(prev => new Map(prev).set(equipoId, datosActualizados));
                    onCorregir?.(equipoId, datosActualizados);
                }}
                onClose={handleModalClose}
                onCancelar={handleModalCancelar}
            />
        </div>
    );
};

export default EquipoUltimasCal;
